import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { isChunkLoadError, hasRecentReload, clearReloadFlag } from '@/lib/lazyWithRetry';

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

const ErrorFallback = ({ error, resetError }: FallbackProps) => {
  useEffect(() => {
    if (!isChunkLoadError(error)) return;
    if (!hasRecentReload()) {
      try { sessionStorage.setItem('dextoro_chunk_reload', String(Date.now())); } catch { /* sessionStorage unavailable */ }
      window.location.reload();
      return;
    }
    clearReloadFlag();
  }, [error]);

  const handleReport = () => {
    Sentry.showReportDialog({
      eventId: Sentry.lastEventId(),
      title: 'Something went wrong',
      subtitle: 'Our team has been notified. You can also share additional details.',
      subtitle2: '',
    });
  };

  /** Clear all browser caches and force a fresh page load */
  const handleClearCacheReload = async () => {
    try {
      // Clear CacheStorage (service worker caches)
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // Unregister any service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));
      }
    } catch {
      // best-effort
    }
    // Navigate with cache-bust param to force fresh index.html and bypass HTTP cache
    window.location.href = '/?_cb=' + Date.now();
  };

  const isMimeError = /MIME type|module script|not a valid JavaScript/i.test(error.message);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
          <p className="text-muted-foreground">
            We've encountered an unexpected error. Our team has been notified.
          </p>
        </div>

        {import.meta.env.DEV && (
          <div className="bg-muted/50 rounded-lg p-4 text-left">
            <p className="text-sm font-mono text-destructive break-all">
              {error.message}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {isMimeError ? (
            <Button onClick={handleClearCacheReload} variant="default">
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear cache &amp; reload
            </Button>
          ) : (
            <Button onClick={resetError} variant="default">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try again
            </Button>
          )}
          <Button onClick={handleReport} variant="outline">
            Report issue
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          {isMimeError
            ? 'A cached update is causing issues. Clearing your cache should fix it.'
            : 'If this keeps happening, try refreshing the page or clearing your browser cache.'}
        </p>
      </div>
    </div>
  );
};

interface SentryErrorBoundaryProps {
  children: React.ReactNode;
}

export const SentryErrorBoundary = ({ children }: SentryErrorBoundaryProps) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} resetError={resetError} />
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
